<template>
  <ViewWrapper
    is-full-height
    :breadcrumbs="{
      title: $t('product.EDIT_EVENT'),
      backButton: { isNativeBack: true },
    }"
  >
    <ProductHeader
      :title="productTitleForContentLocale"
      :description="productDescriptionForContentLocale"
    >
      <template #image>
        <ImageUpload
          :read-only="!hasCoursesWritePermission"
          header-menu-inline
          :existing-image="event.product_image_url"
          @onImageUpload="updateEventImage"
        >
          <template #header-title>
            {{ $t('image.PRODUCT_IMAGE_FILE') }}
          </template>
          <template #header-tooltip-recommended-size>
            {{ $t('image.RECOMMENDED_SIZE') }}
          </template>
        </ImageUpload>
      </template>
    </ProductHeader>

    <BaseTabs
      :options="tabItems"
      no-box-container
      :keep-tabs="false"
      :max-tabs="5"
    >
      <template #default="{ activeTab: tabName }">
        <div class="tab-body">
          <div v-if="tabName === 'dates'" class="dates">
            <router-view :key="$route.fullPath" />
          </div>

          <div v-else-if="tabName === 'language'" class="tab-body__layout">
            <TitleDescriptionLocalized
              :titles="newProductTitles"
              :descriptions="newProductDescriptions"
              :header-title="$t('TITLE_AND_DESCRIPTION')"
              :title-label="$t('TITLE')"
              :description-label="$t('DESCRIPTION')"
              :heading-level="3"
              @onUpdatelocalizedTitle="updateLocalizedTitle"
              @onUpdatelocalizedDescription="updateLocalizedDescriptions"
            />

            <div class="tab-body__action-buttons">
              <BaseButton
                outline
                tabindex="-1"
                size="large"
                :disabled="isSubmittingEventTexts"
                @click="resetTitleAndDescription"
              >
                {{ $t('RESET') }}
              </BaseButton>

              <BaseButton
                :loading="isSubmittingEventTexts"
                :data-cy="'update-' + event.id"
                theme="blue"
                size="large"
                :disabled="isSubmittingEventTexts"
                @click="updateEventTexts"
              >
                {{ $t('SAVE') }}
              </BaseButton>
            </div>
          </div>

          <div v-else-if="tabName === 'access'" class="tab-body__layout">
            <ProductPolicies
              :title="$t('events.ACCESS')"
              :product-id="String(productId)"
              :is-self-served-product="event.self_serve"
              @onChange="handleProductPolicyChange"
            />

            <div class="tab-body__action-buttons">
              <!--    TODO: Enable cancelling          -->
              <BaseButton
                :disabled="!isProductPolicyChanges || isSubmittingPolicies"
                outline
                tabindex="-1"
                size="large"
                @click="resetPolicies"
              >
                {{ $t('RESET') }}
              </BaseButton>

              <BaseButton
                :loading="isSubmittingPolicies"
                :disabled="!isProductPolicyChanges || isSubmittingPolicies"
                :data-cy="'update-' + event.id"
                theme="blue"
                size="large"
                @click="submitPolicies"
              >
                {{ $t('SAVE') }}
              </BaseButton>
            </div>
          </div>

          <div v-else class="tab-body__layout settings-page scrollable">
            <h3 class="description__header-heading">
              {{ $t('SETTINGS') }}
            </h3>

            <div class="settings-page__list">
              <ActionList
                :heading-level="4"
                :heading-text="$t('events.USER_COMPLETION')"
              >
                <ActionListItem
                  :title="$t('productSettings.STARS_LABEL')"
                  :description="$t('productSettings.STARS_DESCRIPTION_EVENT')"
                  @onClick="onOpenDeleteModal"
                >
                  <AttensiToggle
                    manual-mode
                    name="no-score-toggle"
                    size="small"
                    :disabled="true"
                    :toggled="!event.no_score"
                  />
                </ActionListItem>

                <ActionListItem
                  :title="$t('productSettings.MANAGER_APPROVAL_LABEL_EVENT')"
                  :description="
                    $t('productSettings.MANAGER_APPROVAL_DESCRIPTION_EVENT')
                  "
                  @onClick="onOpenDeleteModal"
                >
                  <AttensiToggle
                    name="manager-approval-toggle"
                    size="small"
                    :disabled="hasFirstEventPassed"
                    :toggled="event.manager_approval"
                    @toggle="updateManagerApproval"
                  />
                </ActionListItem>
              </ActionList>

              <ActionList
                :heading-level="4"
                :heading-text="$t('ADVANCED_SETTINGS')"
              >
                <ActionListItem
                  :title="$t('events.DELETE')"
                  :description="
                    $t('events.DELETE_EVENT_DESCRIPTION', {
                      productType: 'event',
                    })
                  "
                >
                  <BaseButton
                    :data-cy="'delete-' + event.id"
                    theme="red"
                    outline
                    @click="() => (showDeleteEventModal = true)"
                  >
                    {{ $t('events.DELETE') }}
                  </BaseButton>
                </ActionListItem>
              </ActionList>
            </div>

            <DialogModal
              v-if="showDeleteEventModal"
              :title="$t('CONFIRM_DELETION')"
              :description="modalDeleteDescription"
              :cta-button-text="$t('DELETE')"
              :cancel-button-text="$t('buttons.CANCEL')"
              cta-button-theme="red"
              @onClose="() => (showDeleteEventModal = false)"
              @onConfirm="deleteEvent"
            />
          </div>
        </div>
      </template>
    </BaseTabs>
  </ViewWrapper>
</template>

<script setup lang="ts">
import TitleDescriptionLocalized from '@/components/form/TitleDescriptionLocalized.vue'
import ActionList from '@/components/generics/action_list/ActionList.vue'
import ActionListItem from '@/components/generics/action_list/ActionListItem.vue'
import BaseButton from '@/components/generics/base/BaseButton.vue'
import BaseTabs from '@/components/generics/base/BaseTabs.vue'
import ImageUpload from '@/components/generics/imageUpload/ImageUpload.vue'
import DialogModal from '@/components/generics/modals/DialogModal.vue'
import ViewWrapper from '@/components/layout/view_wrapper/ViewWrapper.vue'
import ProductHeader from '@/components/product/header/ProductHeader.vue'
import ProductPolicies from '@/components/product/product_components/component_policies/ProductPolicies.vue'
import { useProductPolicies } from '@/hooks/swr/useProductPolicies.ts'
import { useContentLocale } from '@/hooks/useContentLocale.ts'
import { useDeleteProduct } from '@/hooks/useDeleteProduct'
import { useI18n } from '@/hooks/useI18N.ts'
import { useToast } from '@/hooks/useToast.ts'
import { useStore } from '@/hooks/vue-utils/useVuex.ts'
import ApiService from '@/service/api.service.js'
import {
  deleteProductFromPolicy,
  patchProductPolicy,
  postProductToPolicy,
} from '@/service/v3/endpoints/policies.ts'
import { AttensiToggle } from '@attensi/materia'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useEventQuery } from '@/queries/events/eventQueries.ts'
import { useQueryClient } from '@tanstack/vue-query'
import { QUERY_KEY_EVENT } from '@/queries/events/eventKeys.ts'
import { useUserPermission } from '@/hooks/usePermission.ts'

const i18n = useI18n()
const store = useStore()
const route = useRoute()
const router = useRouter()
const { deleteProduct } = useDeleteProduct()
const { doToast } = useToast()
const productId = route.params.productId
const { canWrite: hasCoursesWritePermission } = useUserPermission('courses')

const queryClient = useQueryClient()

const tabItems = [
  { display: i18n.t('DATES'), value: 'dates' },
  {
    display: i18n.t('TITLE_AND_DESCRIPTION'),
    value: 'language',
    hideTab: !hasCoursesWritePermission.value,
  },
  {
    display: i18n.t('ACCESS'),
    value: 'access',
    hideTab: !hasCoursesWritePermission.value,
  },
  {
    display: i18n.t('SETTINGS_TAB'),
    value: 'settings',
    hideTab: !hasCoursesWritePermission.value,
  },
]

const { data: event } = useEventQuery(Number(productId))

const { productPolicies, mutate: mutateProductPolicies } = useProductPolicies(
  route.params.productId
)

const newProductPolicies = ref<any>([])
const isSubmitting = ref(false)
const isSubmittingEventTexts = ref(false)
const isSubmittingPolicies = ref(false)
const isProductPolicyChanges = ref(false)
const isDeleteModalOpen = ref(false)
const showDeleteEventModal = ref(false)
const newProductTitles = ref(event.value?.title ?? {})
const newProductDescriptions = ref(event.value?.description_long ?? {})
const { pickTextFromLocale } = useContentLocale(
  i18n.t('productFilters.MISSING_TITLES_OR_DESCRIPTIONS')
)

watch(
  () => event.value,
  (newEvent) => {
    newProductTitles.value = { ...newEvent.title }
    newProductDescriptions.value = { ...newEvent.description_long }
  }
)

const productTitleForContentLocale = computed(() => {
  return (
    pickTextFromLocale(event.value?.title) ||
    i18n.t('productFilters.MISSING_TITLES_OR_DESCRIPTIONS')
  )
})

const productDescriptionForContentLocale = computed(
  () =>
    pickTextFromLocale(event.value?.description_long) ||
    i18n.t('productFilters.MISSING_TITLES_OR_DESCRIPTIONS')
)

const modalDeleteDescription = computed(() =>
  i18n.t('product_events.delete.CONFIRM_DESCRIPTION', {
    productTitle: productTitleForContentLocale.value,
  })
)

const hasFirstEventPassed = computed(() => {
  return event.value.courses?.some((course) => {
    return course.state === 'expired'
  })
})

const resetTitleAndDescription = () => {
  newProductTitles.value = { ...event.value.title }
  newProductDescriptions.value = { ...event.value.description_long }
}

const updateLocalizedTitle = ({ locale, value }) => {
  newProductTitles.value[locale] = value
}

const updateLocalizedDescriptions = ({
  locale,
  value,
}: {
  locale: string
  value: string
}) => {
  newProductDescriptions.value[locale] = value
}

const updateManagerApproval = async ({ value }) => {
  const formData = new FormData()
  formData.append('product[manager_approval]', value)

  await patchProductByFormData(formData)
  await queryClient.invalidateQueries({
    queryKey: [QUERY_KEY_EVENT, productId],
  })
}

async function updateEventImage({ values }) {
  const formData = new FormData()
  formData.append('product[image_file]', values)

  await patchProductByFormData(formData)
  await queryClient.invalidateQueries({
    queryKey: [QUERY_KEY_EVENT, productId],
  })
}

async function patchProductByFormData(formData: FormData) {
  try {
    await ApiService.updateProductById(productId, formData)
    doToast({ text: i18n.t('UPDATE_SUCCESS') })
  } catch (error) {
    console.error('Error while updating product by id', error)
    doToast({
      text: i18n.t('UPDATE_ERROR', { error: error.message }),
      theme: 'error',
    })
  }
}

async function updateEventTexts() {
  isSubmittingEventTexts.value = true

  const formData = new FormData()
  Object.entries(newProductTitles.value).forEach(([localeCode, title]) => {
    formData.append(`product[title][${localeCode}]`, title)
  })
  Object.entries(newProductDescriptions.value).forEach(
    ([localeCode, description]) => {
      formData.append(`product[description_long][${localeCode}]`, description)
    }
  )

  await patchProductByFormData(formData)
  await queryClient.invalidateQueries({
    queryKey: [QUERY_KEY_EVENT, productId],
  })
  isSubmittingEventTexts.value = false
}

const resetPolicies = () => {
  mutateProductPolicies()
  newProductPolicies.value = []
  isProductPolicyChanges.value = false
}

async function submitPolicies() {
  isSubmittingPolicies.value = true
  const policiesToCreate = []
  const policiesToUpdate = []
  const policiesToDelete = []

  // Look through new array, compare and add or update policies
  newProductPolicies.value.forEach((policy) => {
    const indexOfExistingPolicy = productPolicies.value.findIndex(
      (existingPolicy) => existingPolicy.id === policy.id
    )
    if (indexOfExistingPolicy < 0) {
      policiesToCreate.push(policy)
      return
    }

    const isPolicyChanged =
      productPolicies.value[indexOfExistingPolicy].state !== policy.value
    if (isPolicyChanged) {
      policiesToUpdate.push(policy)
    }
  })

  productPolicies.value.forEach((existingPolicy) => {
    const isExistingPolicyStillPresent = newProductPolicies.value.some(
      (newPolicy) => newPolicy.id === existingPolicy.id
    )
    if (!isExistingPolicyStillPresent) {
      // We need to normalize the state her to match the option values
      // TODO: add types to help clarify this
      policiesToDelete.push({
        id: existingPolicy.id,
        name: existingPolicy.name,
        value: existingPolicy.state,
      })
    }
  })

  await Promise.all([
    ...policiesToUpdate.map((policy) =>
      patchProductPolicy({
        policyId: Number(policy.id),
        productId: Number(productId),
        policyState: policy.value,
      })
    ),
    ...policiesToCreate.map((policy) =>
      postProductToPolicy({
        policyId: Number(policy.id),
        productId: Number(productId),
        policyState: policy.value,
      })
    ),
    ...policiesToDelete.map((policy) =>
      deleteProductFromPolicy({
        policyId: Number(policy.id),
        productId: Number(productId),
      })
    ),
  ])
    .then(() => doToast({ text: i18n.t('UPDATE_SUCCESS') }))
    .catch((error) => {
      console.error('Error while updating product policies', error)
      doToast(
        {
          text: i18n.t('UPDATE_ERROR', { error: error.message }),
        },
        { displayDuration: 4000, theme: 'error' }
      )
    })
    .finally(() => {
      isSubmittingPolicies.value = false
    })

  resetPolicies()
  isSubmitting.value = false
}

async function deleteEvent() {
  const isProductDeleted = await deleteProduct(
    event.value.id,
    productTitleForContentLocale
  )
  if (isProductDeleted) {
    store.dispatch('setDeletedProduct', event.value.id)
    router.back()
  }
}

function handleProductPolicyChange(pickedPolicies) {
  newProductPolicies.value = pickedPolicies
  isProductPolicyChanges.value = true
}

function onOpenDeleteModal() {
  isDeleteModalOpen.value = true
}
</script>

<style lang="scss" scoped>
.tab-body {
  display: flex;
  padding-top: $spacing-3xs;
  flex: 1 1 0;

  &__layout {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    gap: $spacing-3xs;
  }

  &__action-buttons {
    display: flex;
    gap: $spacing-3xs;
    justify-content: end;
  }
}

.settings-page {
  display: flex;
  flex-direction: column;
  gap: $spacing-s;
  max-width: $medium-desktop-width-max;
  padding: $spacing-s;

  &__list {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;
  }
}

.scrollable {
  overflow-y: auto;
  overflow: hidden;
  height: 100%;
}

.dates {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  background: $color-paper;
  border: $border-default;
  border-radius: $border-radius-xl;
  overflow: hidden;
  padding: $spacing-xs;
}
</style>
